import {
  type Column,
  type TableFeature,
  type RowData,
  assignPrototypeAPIs,
  assignTableAPIs,
  type Table,
  type CellData,
  type Header,
} from '@tanstack/react-table';
import {
  orderColumns,
  table_getAllLeafColumns,
  table_setColumnOrder,
} from '@tanstack/react-table/static-functions';

import { type TDataTableFeatures } from '../types/features.types.js';
import {
  createPrototypeConstructor,
  createTableConstructor,
} from '../types/table.types.js';
import {
  canMoveColumnLeft,
  canMoveColumnRight,
  getColumnsToCompareForMove,
  moveColumnLeft,
  moveColumnRight,
} from './column-order-utils.js';

/**
 * Overwrite the order columns fn from react table so that builtin columns are placed correctly
 * @internal
 */
export function table_getOrderColumnsFn<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
): (
  columns: Array<Column<TDataTableFeatures, TData, unknown>>,
) => Column<TDataTableFeatures, TData, unknown>[] {
  const columnOrder = table.atoms.columnOrder?.get() ?? [];

  return (columns: Array<Column<TDataTableFeatures, TData, unknown>>) => {
    const prefixColumns: Array<Column<TDataTableFeatures, TData, unknown>> = [];
    const suffixColumns: Array<Column<TDataTableFeatures, TData, unknown>> = [];
    let orderedColumns: Array<Column<TDataTableFeatures, TData, unknown>> =
      columns;

    // If there is no order, return the normal columns
    if (columnOrder.length) {
      const columnsMap = new Map();

      columns.forEach((column) => {
        const builtinPlacement = column.columnDef.meta?.builtinPlacement;
        if (builtinPlacement === 'prefix') {
          prefixColumns.push(column);
        } else if (builtinPlacement === 'suffix') {
          suffixColumns.push(column);
        } else {
          columnsMap.set(column.id, column);
        }
      });

      orderedColumns = [
        ...prefixColumns,
        ...columnOrder.flatMap((id) => columnsMap.get(id) ?? []),
        ...suffixColumns,
      ];
    }

    return orderColumns(table, orderedColumns);
  };
}

/**
 * Helper function that provides an array of all leaf-node columns, excluding builtin columns.
 * @internal
 */
export function table_getLeafColumnsWithoutBuiltin<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
): Column<TDataTableFeatures, TData, unknown>[] {
  return table_getAllLeafColumns(table).filter(
    (leafColumn) => !leafColumn.columnDef.meta?.builtin,
  );
}

/**
 * Helper function to update the currently dragged header in the state.
 * @internal
 */
export function table_setDraggedHeaderColumn<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  column: Column<TDataTableFeatures, TData>,
): void {
  table.baseAtoms.draggedHeaderColumn.set(
    column as Column<TDataTableFeatures, RowData>,
  );
}

/**
 * Helper function to create the restrict drag and drop modifier based on the
 * current table state.
 * @internal
 */
export function header_getColumnDragAndDropMode<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  header: Header<TDataTableFeatures, TData, TValue>,
): 'none' | 'only' | 'enabled' {
  const isDragAndDropDisabled =
    header.table.options.enableColumnOrdering !== true ||
    header.column.columnDef.enableUserActions === false ||
    header.getIsPinned() ||
    (!column_getCanMoveColumnLeft(header.column) &&
      !column_getCanMoveColumnRight(header.column));

  if (isDragAndDropDisabled) {
    return 'none';
  }

  if (
    !header.column.getCanColumnActions() &&
    !header.getCanSortByHeaderClick()
  ) {
    return 'only';
  }

  return 'enabled';
}

/**
 * Helper function that determines whether drag and drop is disabled on the header (if a child column is dragged).
 * @internal
 */
export function header_getIsDragAndDropDisabled<
  TData extends RowData,
  TValue extends CellData = CellData,
>(header: Header<TDataTableFeatures, TData, TValue>): boolean {
  const draggedHeaderColumn = header.table.atoms.draggedHeaderColumn?.get();
  const draggedHeaderParentId = draggedHeaderColumn?.parent?.id;
  const draggedHeaderId = draggedHeaderColumn?.id;
  const isPinnedHeader = !!header.column.getIsPinned();

  if (draggedHeaderId && isPinnedHeader) {
    return true;
  }

  if (
    draggedHeaderParentId === undefined ||
    draggedHeaderParentId === header.column.id ||
    draggedHeaderParentId === header.column.parent?.id
  ) {
    return false;
  }
  return true;
}

/**
 * Helper function to update the column order state with a new column order object or a functional update.
 * @internal
 */
export function column_getCanMoveColumnLeft<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>): boolean {
  const columnsToCompare = getColumnsToCompareForMove(column.table, column);
  return canMoveColumnLeft(column, columnsToCompare);
}

/**
 * Helper function to update the column order state with a new column order object or a functional update.
 * @internal
 */
export function column_getCanMoveColumnRight<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>): boolean {
  const columnsToCompare = getColumnsToCompareForMove(column.table, column);
  return canMoveColumnRight(column, columnsToCompare);
}

/**
 * Moves the current column one position to the left and updates the table state.
 *
 * Depending on whether the column is a nested column or a standalone column,
 * whether its neighboring columns are pinned, hidden, or nested,
 * we need to consider different scenarios to calculate the new column order correctly.
 *
 * CASE1: Column itself is a nested child column.
 *  - Constraint1: Can only move within the parent group.
 *  - Constraint2: Skip pinned siblings.
 *  - Constraint3: Skip hidden siblings.
 *
 * CASE2: Column is a standalone column or a parent column.
 *  - Constraint1: Skip entire nested columns as a whole.
 *  - Constraint2: Skip pinned columns.
 *  - Constraint3: Skip hidden columns.
 * @internal
 */
export function column_moveColumnLeft<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>): void {
  // Get the current table state needed for column reordering
  const leafColumns = column.table.getLeafColumnsWithoutBuiltin() as Column<
    TDataTableFeatures,
    TData,
    TValue
  >[];
  const currentColumnOrder = column.table.atoms.columnOrder?.get() ?? [];
  const columnsPinned = column.table.atoms.columnPinning?.get() ?? {
    left: [],
    right: [],
  };

  const newColumnOrder = moveColumnLeft<TData, TValue>(
    column,
    currentColumnOrder,
    columnsPinned,
    leafColumns,
  );
  table_setColumnOrder(column.table, newColumnOrder);
}

/**
 * Moves the current column one position to the right and updates the table state.
 *
 * Depending on whether the column is a nested column or a standalone column,
 * whether its neighboring columns are pinned, hidden, or nested,
 * we need to consider different scenarios to calculate the new column order correctly.
 *
 * CASE1: Column itself is a nested child column.
 *  - Constraint1: Can only move within the parent group.
 *  - Constraint2: Skip pinned siblings.
 *  - Constraint3: Skip hidden siblings.
 *
 * CASE2: Column is a standalone column or a parent column.
 *  - Constraint1: Skip entire nested columns as a whole.
 *  - Constraint2: Skip pinned columns.
 *  - Constraint3: Skip hidden columns.
 * @internal
 */
export function column_moveColumnRight<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>): void {
  // Get the current table state needed for column reordering
  const leafColumns = column.table.getLeafColumnsWithoutBuiltin() as Column<
    TDataTableFeatures,
    TData,
    TValue
  >[];
  const currentColumnOrder = column.table.atoms.columnOrder?.get() ?? [];
  const columnsPinned = column.table.atoms.columnPinning?.get() ?? {
    left: [],
    right: [],
  };
  const newColumnOrder = moveColumnRight<TData, TValue>(
    column,
    currentColumnOrder,
    columnsPinned,
    leafColumns,
  );
  table_setColumnOrder(column.table, newColumnOrder);
}

/**
 * Constructs the column order feature for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructColumnOrderFeature(): TableFeature {
  return {
    getInitialState: (initialState) => {
      return {
        draggedHeaderColumn: null,
        ...initialState,
      };
    },
    assignColumnPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('DataTableColumnOrderFeature', prototype, table, {
          column_getCanMoveColumnLeft: {
            fn: (column) => column_getCanMoveColumnLeft(column),
          },
          column_getCanMoveColumnRight: {
            fn: (column) => column_getCanMoveColumnRight(column),
          },
          column_moveColumnLeft: {
            fn: (column) => column_moveColumnLeft(column),
          },
          column_moveColumnRight: {
            fn: (column) => column_moveColumnRight(column),
          },
        });
      },
    ),
    assignHeaderPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('DataTableColumnOrderFeature', prototype, table, {
          header_getColumnDragAndDropMode: {
            fn: (header) => header_getColumnDragAndDropMode(header),
          },
          header_getIsDragAndDropDisabled: {
            fn: (header) => header_getIsDragAndDropDisabled(header),
          },
        });
      },
    ),
    constructTableAPIs: createTableConstructor(
      (table: Table<TDataTableFeatures, RowData>) => {
        assignTableAPIs('DataTableColumnOrderFeature', table, {
          table_getLeafColumnsWithoutBuiltin: {
            fn: () => table_getLeafColumnsWithoutBuiltin(table),
          },
          table_getOrderColumnsFn: {
            fn: () => table_getOrderColumnsFn(table),
            // Upstream also keys this on the grouping state. The DataTable does
            // not register `columnGroupingFeature`, and the ordering function
            // above reads nothing but the column order.
            memoDeps: () => [table.atoms.columnOrder?.get()],
          },
          table_setDraggedHeaderColumn: {
            fn: (column: Column<TDataTableFeatures, RowData>) => {
              table_setDraggedHeaderColumn<RowData>(table, column);
            },
          },
        });
      },
    ),
  };
}

/**
 * Additional feature implementation for column order.
 * @internal
 */
export const DataTableColumnOrderFeature = constructColumnOrderFeature();
